import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { colors, Spinner, Surface } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { HeaderWithBackNavigation } from '@app/components/HeaderWithBackNavigation';
import { useScreenResolution } from '@app/hooks';
import {
  addPlanToCart,
  ChangePlanSteps,
  selectChangePlan,
  selectChosenPlan,
  setChangePlanStep,
  submitOrder,
} from '@app/redux/changePlan';
import { selectConfig } from '@app/redux/config';
import { sendMessageToMobileApp } from '@app/redux/crossPlatformCommunication';

import { Platform } from '@mfe/shared/schema-types';

import CartSummary from '../../components/CartSummary';
import CartSummaryWrapper from '../../components/CartSummaryWrapper';
import CollapsibleCartSummary from '../../components/CollapsibleCartSummary';
import ErrorWithRefresh from '../../components/Errors/ErrorWithRefresh';
import OrderErrorContent from '../../components/Errors/OrderErrorContent';
import { useConsentCheck } from '../../hooks/use-consent-check';
import OrderDetailsCard from './components/OrderDetailsCard';
import SubmitSection from './components/SubmitSection';

import styles from './styles.module.scss';

type OrderReviewProps = {
  handleBackNavigation: () => void;
};

export type ModalTypes = 'terms&conditions' | 'calculationModal';

const OrderReview = ({ handleBackNavigation }: OrderReviewProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('ChangePlan');

  const [type, setType] = useState<ModalTypes | null>(null);
  const isTermsAndConditionsModalOpen = type === 'terms&conditions';
  const isCalculationModalOpen = type === 'calculationModal';
  const isModalOpen = isTermsAndConditionsModalOpen || isCalculationModalOpen;

  const { isExtraSmall, isSmall, isMedium } = useScreenResolution();
  const isMobile = isSmall || isExtraSmall;
  const isMobileOrTablet = isMobile || isMedium;

  const {
    requiresEquipmentChange,
    loading,
    error,
    eicMessageLoading,
    isEicChecked,
    hasAttemptedSubmit,
    cart: { eicRequired },
  } = useSelector(selectChangePlan);
  const selectedPlan = useSelector(selectChosenPlan);
  const { platform } = useSelector(selectConfig);
  const disclosureConsent = useConsentCheck(isEicChecked, eicRequired);

  const smallScreenLoadingStyles =
    isMobile && loading ? { marginTop: 0, borderTop: `1px solid ${colors['gray'][200]}` } : {};

  const handleNextStepNavigation = () => {
    disclosureConsent.checkConsent();
    if (requiresEquipmentChange) {
      dispatch(setChangePlanStep(ChangePlanSteps.SELECT_SCHEDULE));
    } else {
      dispatch(submitOrder(undefined));
      dispatch(setChangePlanStep(ChangePlanSteps.ORDER_OUTCOME));
    }
  };

  const handleRefreshOnErrorPage = () => {
    dispatch(addPlanToCart({ newProductTypeId: selectedPlan.id }));
  };

  const openModal = (e: React.MouseEvent<HTMLElement>, modalType: ModalTypes) => {
    e.preventDefault();
    dispatch(
      sendMessageToMobileApp({
        type: 'set-refresh-control-status',
        data: { refreshControlEnabled: false },
      })
    );
    setType(modalType);
  };

  if (error) {
    return (
      <ErrorWithRefresh handleRefresh={handleRefreshOnErrorPage} handleGoBack={handleBackNavigation}>
        <OrderErrorContent />
      </ErrorWithRefresh>
    );
  }

  const handleModalClose = () => {
    setType(null);
    if (platform !== Platform.Web && isModalOpen) {
      dispatch(
        sendMessageToMobileApp({
          type: 'set-refresh-control-status',
          data: { refreshControlEnabled: true },
        })
      );
    }
  };

  if (!selectedPlan) {
    return null;
  }

  return (
    <Surface className={styles['wrapper']} variant="secondary">
      {isMobileOrTablet && !loading && (
        <CollapsibleCartSummary selectedPlan={selectedPlan} handleBackNavigation={handleBackNavigation} />
      )}
      <HeaderWithBackNavigation
        handleBackNavigation={handleBackNavigation}
        title={t('orderReview.title')}
        goBackLabel={t('goBack')}
        additionalClassName={styles['page-header']}
        style={smallScreenLoadingStyles}
      />
      {loading || eicMessageLoading ? (
        <div className={styles['spinner-wrapper']}>
          <Spinner description={t('orderReview.loading')} />
        </div>
      ) : (
        <div className={styles['page-content']}>
          <OrderDetailsCard
            openModal={openModal}
            closeModal={handleModalClose}
            handlePlacePlanOrder={handleNextStepNavigation}
            isCalculationModalOpen={isCalculationModalOpen}
            isTermsAndConditionsModalOpen={isTermsAndConditionsModalOpen}
            hasAttemptedSubmit={hasAttemptedSubmit}
          />
          {isMobileOrTablet ? (
            !requiresEquipmentChange && (
              <CartSummaryWrapper>
                <CartSummary
                  selectedPlan={selectedPlan}
                  handleBackNavigation={handleBackNavigation}
                  requiresEquipmentChange={requiresEquipmentChange}
                />

                <hr style={{ marginTop: '24px' }} />

                <SubmitSection
                  handlePlacePlanOrder={handleNextStepNavigation}
                  handleBackNavigation={handleBackNavigation}
                  requiresEquipmentChange={requiresEquipmentChange}
                  isEicChecked={isEicChecked}
                  hasAttemptedSubmit={hasAttemptedSubmit}
                />
              </CartSummaryWrapper>
            )
          ) : (
            <CartSummary
              selectedPlan={selectedPlan}
              handleBackNavigation={handleBackNavigation}
              requiresEquipmentChange={requiresEquipmentChange}
            />
          )}
        </div>
      )}
    </Surface>
  );
};

export default OrderReview;
